import { PHASE_CALIBRATION } from '../../../../constants';
import { getAircraftInfo } from '../../../../helpers/getAircraftInfo';
import { getCalibrationMetrics } from '../../../../helpers/getCalibrationMetrics';
import { getHealthRings } from '../../../../helpers/getHealthRings';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import RingGauge from 'pages/MaintenancePage/components/RingGauge';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const TabPanelHealth: FunctionComponent<Props> = ({
    className = '',
    drone,
    phase,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const rings = getHealthRings({
        drone,
        labels: {
            motors: t.MOTORS_LABEL,
            props: t.PROPS_LABEL,
            pumps: t.PUMPS_LABEL
        },
        serviceIntervals
    });
    const batteryInterval = serviceIntervals.battery?.value ?? 0;
    const calibrationMetrics =
        phase >= PHASE_CALIBRATION
            ? getCalibrationMetrics({
                  drone,
                  labels: {
                      daysAgoSuffix: t.UNIT_DAYS_AGO,
                      flow: t.CALIBRATION_FLOW,
                      level: t.CALIBRATION_LEVEL,
                      magnetometer: t.CALIBRATION_MAGNETOMETER,
                      neverText: t.NEVER_RECORDED,
                      pump: t.CALIBRATION_PUMP,
                      weight: t.CALIBRATION_WEIGHT
                  }
              })
            : [];
    const aircraftInfo = getAircraftInfo({
        drone,
        labels: {
            droneSerial: t.DRONE_SERIAL,
            emDash: t.EM_DASH,
            faaRegistration: t.FAA_REGISTRATION,
            firmwareVersion: t.FIRMWARE_VERSION,
            unitDays: t.UNIT_DAYS,
            warrantyEnd: t.WARRANTY_END,
            warrantyExpired: t.WARRANTY_EXPIRED,
            warrantyRemaining: t.WARRANTY_REMAINING,
            warrantyStart: t.WARRANTY_START
        },
        now: new Date()
    });
    const metrics = [
        { label: t.TOTAL_FLIGHT_HOURS, value: `${drone.flightHours} h` },
        { label: t.BATTERY_CYCLES, value: `${drone.usage.battery ?? 0} / ${batteryInterval}` },
        ...calibrationMetrics,
        ...aircraftInfo
    ];

    return (
        <VerticalContainer className={className} testId={testId}>
            <HorizontalContainer className={styles.healthRings}>
                {rings.map(ring => (
                    <RingGauge
                        key={ring.label}
                        label={ring.label}
                        notConfiguredText={t.NOT_CONFIGURED}
                        serviceLife={ring.serviceLife}
                        size='large'
                    />
                ))}
            </HorizontalContainer>

            <VerticalContainer className={styles.metricsTable}>
                {metrics.map(metric => (
                    <HorizontalContainer className={styles.metricRow} key={metric.label}>
                        <Wrapper className={styles.metricLabel}>{metric.label}</Wrapper>

                        <Wrapper className={styles.metricValue}>{metric.value}</Wrapper>
                    </HorizontalContainer>
                ))}
            </VerticalContainer>
        </VerticalContainer>
    );
};

export default TabPanelHealth;
